import SessionManager from "@Core/session";
import { IActivityDetails } from "@Models/IActivityDetails";
import { ITaskDetails, ITaskSubmissionDetails } from "@Models/ITaskDetails";
import { IColumnData, arrayToMapByKey, getDuration, usePrevious, useSearchParams } from "@Utils";
import {CheckOutlined, PlaySquareOutlined} from "@ant-design/icons";
import { Checkbox, CheckboxProps, Tooltip } from "antd";
import React, { useEffect, useState } from "react";
import { ActivityCheckboxTooltip } from "../assessmentConstants";
import { useSubmitTasks } from "../mutations";
import { IUseAssessmentData } from "../types";
import { useTaskAssessments } from "./../queries";
import useStudentAssessments from "./useStudentAssessments";
import TickIcon from "@Components/shared/TickIcon";
import CrossIcon from "@Components/shared/CrossIcon";
import { TableRowSelection } from "antd/lib/table/interface";

interface IUseStudentTaskSubmissionData extends IUseAssessmentData<ITaskDetails> {
    tableData: {
        activityList: {
            columnData: IColumnData<IActivityDetails>[];
            data: IActivityDetails[];
            isLoading: boolean;
            isFetched: boolean;
            rowSelection: TableRowSelection<IActivityDetails>;
        };
        taskTable: {
            columnData: IColumnData<ITaskDetails & { activityLabel: string }>[];
            data: (ITaskDetails & { activityLabel: string })[];
            isLoading: boolean;
        };
    };
    videoActivity: IActivityDetails;

    onRowClick: (activity: IActivityDetails) => void;
    onSubmit: () => void;
}

interface IOriginalNodeProps extends Partial<Omit<CheckboxProps, "checked" | "defaultChecked">> {
    tooltipTitle: string;
}

const useStudentTaskSubmission = (): IUseStudentTaskSubmissionData => {
    const { userId } = SessionManager.getServiceUser();

    // ** Search params ** //
    const [searchParams, setSearchParams] = useSearchParams();
    const tab: string = searchParams.get("tab");
    const assessmentId = searchParams.get("assessment");

    // ** State Hooks ** //
    const [activitySelectionsMap, setActivitySelectionsMap] = useState<
        Map<ITaskDetails["activityId"], Set<IActivityDetails["activityInstanceId"]>>
    >(new Map());
    const [videoActivity, setVideoActivity] = useState<IActivityDetails>();

    // ** Queries and Mutations ** //
    const {
        assessmentInstances,
        isFetched: areStudentsAssessmentsFetched,
        isFetching,
        isLoading,
    } = useStudentAssessments();
    const { mutate: submitTasks, isLoading: isSubmitting } = useSubmitTasks(userId, assessmentId);

    const assessmentInstance = !!assessmentId
        ? assessmentInstances?.find((ai) => ai.assessmentId === assessmentId)
        : null;

    const {
        activityInstances,
        isFetched,
        isLoading: isLoadingTaskAssessments,
        preCheckedInstances,
        taskByActivityId,
        taskDetails,
    } = useTaskAssessments(userId, assessmentInstance);

    const total = taskDetails.reduce((acc, cur) => acc + cur.quantity, 0);
    const allSelections = Array.from(activitySelectionsMap).reduce((acc, cur) => acc + cur[1].size, 0);

    const wasFetched = usePrevious(isFetched);

    // reset selections when the tab changes
    useEffect(() => {
        setActivitySelectionsMap(preCheckedInstances);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tab]);

    // set the selections once the data is fetched
    useEffect(() => {
        if (isFetched && isFetched !== wasFetched) {
            setActivitySelectionsMap(preCheckedInstances);
        }
    }, [isFetched, preCheckedInstances, wasFetched]);

    // ** Column Data for Activity List Table ** //
    const activityListColumnData: IColumnData<IActivityDetails>[] = [
        { key: "activityLabel", title: "Activity", align: "center" },
        { key: "creationDate", title: "Date", render: "date", align: "center" },
        { key: "duration", render: (_, record) => getDuration(record.duration) },
        {
            key: "mediaUrl",
            title: <PlaySquareOutlined style={{ color: "#888" }} title={"Recorded"} />,
            render: (mediaUrl: IActivityDetails["mediaUrl"]) => (mediaUrl ? <CheckOutlined /> : ""),
            width: 75,
            align: "center",
        },
    ];

    // ** Column Data for Task Table ** //

    const taskColumnData: IColumnData<ITaskDetails & { activityLabel: string }>[] = [
        {
            key: "activityLabel",
            title: "Task",
            align: "left",
        },
        {
            key: "quantity",
            render: (_, task) => `${activitySelectionsMap.get(task.activityId)?.size ?? 0} / ${task.quantity}`,
            align: "center",
        },
        {
            key: "recorded",
            render: (_, task) => (task.recorded ? <TickIcon /> : <CrossIcon />),
            align: "center",
        },
        {
            key: "institutional",
            render: (_, task) => (task.institutional ? <TickIcon /> : <CrossIcon />),
            align: "center",
        },
        {
            key: "automated",
            render: (_, task) => (task.automated ? <TickIcon /> : <CrossIcon />),
            align: "center",
        },
    ];

    // ** Event Handlers ** //
    const handleSubmitTasks = () => {
        if (!userId || !assessmentId) return;

        const taskSubmissionDetails: ITaskSubmissionDetails[] = [];
        activitySelectionsMap.forEach((set, activityId) => {
            const taskId = taskByActivityId.get(activityId).taskId;
            set.forEach((activityInstanceId) => {
                taskSubmissionDetails.push({
                    activityInstanceId,
                    taskId,
                });
            });
        });

        submitTasks(
            { model: taskSubmissionDetails },
            {
                onSuccess: () => {
                    setSearchParams({ tab: "assigned" });
                },
            }
        );
    };

    const handleActivityRowClick = (activity: IActivityDetails) => setVideoActivity(activity);

    const rowSelection: TableRowSelection<IActivityDetails> = {
        hideSelectAll: true,
        onChange: (_: React.Key[], selectedRows) => {
            const newMap: Map<unknown, unknown> = arrayToMapByKey(
                selectedRows,
                (d) => d.activityId,
                false,
                "activityInstanceId"
            );
            newMap.forEach((value, key, map) => map.set(key, new Set<string>(value as string)));
            setActivitySelectionsMap(newMap as Map<string, Set<string>>);
        },
        getCheckboxProps: (record) => {
            const isPreChecked = preCheckedInstances.has(record.activityId);
            const selections = activitySelectionsMap.get(record.activityId) ?? new Set<string>();
            const selectionsSize: number = selections?.size ?? 0;
            const task = taskByActivityId?.get(record.activityId);
            const isAtCapacity = selectionsSize >= task?.quantity;
            const hasMissingMedia = !!task?.recorded && !record?.mediaUrl;

            const tooltipTitle =
                (isPreChecked && ActivityCheckboxTooltip.Automated) ||
                (isAtCapacity && ActivityCheckboxTooltip.Capacity) ||
                (hasMissingMedia && ActivityCheckboxTooltip.Recorded);

            const isDisabled: boolean =
                isPreChecked || (isAtCapacity && !selections.has(record.activityInstanceId)) || hasMissingMedia;

            return {
                disabled: isDisabled,
                tooltipTitle,
            } satisfies IOriginalNodeProps;
        },
        renderCell: (_isChecked, _record, _index, { props: nodeProps }: React.ReactElement<IOriginalNodeProps>) => {
            const { tooltipTitle } = nodeProps;

            return (
                <Tooltip placement="right" title={tooltipTitle}>
                    <Checkbox {...nodeProps} />
                </Tooltip>
            );
        },
        selectedRowKeys: Array.from(activitySelectionsMap.values())
            .map((a) => Array.from(a))
            .flat(),
    };

    // ** Return Data ** //
    return {
        isDisabled: allSelections < total,
        isFetching,
        isLoading,
        isSubmitting,
        onRowClick: handleActivityRowClick,
        onSubmit: handleSubmitTasks,
        tableData: {
            activityList: {
                columnData: activityListColumnData,
                data: activityInstances,
                isLoading: !isFetched || isLoadingTaskAssessments,
                isFetched,
                rowSelection,
            },
            taskTable: {
                columnData: taskColumnData,
                data: taskDetails,
                isLoading: !areStudentsAssessmentsFetched || isLoadingTaskAssessments,
            },
        },
        videoActivity,
    };
};

export default useStudentTaskSubmission;
